/**
 * 坐标距离计算工具
 * 使用WGS84坐标系（GPS坐标系）进行计算
 */

/**
 * 计算两点间的直线距离（Haversine公式）
 * @param {number} lat1 - 第一点纬度（WGS84）
 * @param {number} lon1 - 第一点经度（WGS84）
 * @param {number} lat2 - 第二点纬度（WGS84）
 * @param {number} lon2 - 第二点经度（WGS84）
 * @returns {number} 距离，单位为米
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // 地球半径，单位：米
  
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return distance;
}

/**
 * 将角度转换为弧度
 * @param {number} degree - 角度
 * @returns {number} 弧度
 */
function toRad(degree) {
  return degree * (Math.PI / 180);
}

/**
 * 格式化距离显示
 * @param {number} distance - 距离，单位为米
 * @param {boolean} useKm - 是否使用公里为单位
 * @returns {string} 格式化后的距离字符串
 */
export function formatDistance(distance, useKm = false) {
  if (distance === null || distance === undefined) {
    return '-';
  }
  
  if (useKm) {
    const km = distance / 1000;
    return km.toFixed(2) + ' km';
  } else {
    return distance.toFixed(2) + ' m';
  }
}

/**
 * 计算路径总距离
 * @param {Array} nodeList - 节点列表，每个节点包含wgs84Lat和wgs84Lng或lat和lng
 * @param {boolean} useKm - 是否使用公里为单位
 * @returns {number} 总距离
 */
export function calculateTotalDistance(nodeList, useKm = false) {
  if (!nodeList || nodeList.length < 2) {
    return 0;
  }
  
  let totalDistance = 0;
  
  for (let i = 0; i < nodeList.length - 1; i++) {
    const currentNode = nodeList[i];
    const nextNode = nodeList[i + 1];
    
    // 按优先级使用WGS84坐标，其次使用GCJ02坐标
    let currentLat, currentLng, nextLat, nextLng;
    
    // 当前nodeLat标
    if (currentNode.wgs84Lat && currentNode.wgs84Lng) {
      currentLat = currentNode.wgs84Lat;
      currentLng = currentNode.wgs84Lng;
    } else if (currentNode.lat && currentNode.lng) {
      currentLat = currentNode.lat;
      currentLng = currentNode.lng;
    } else {
      continue;
    }
    
    // 下next node标
    if (nextNode.wgs84Lat && nextNode.wgs84Lng) {
      nextLat = nextNode.wgs84Lat;
      nextLng = nextNode.wgs84Lng;
    } else if (nextNode.lat && nextNode.lng) {
      nextLat = nextNode.lat;
      nextLng = nextNode.lng;
    } else {
      continue;
    }
    
    const distance = calculateDistance(currentLat, currentLng, nextLat, nextLng);
    totalDistance += distance;
  }
  
  return useKm ? totalDistance / 1000 : totalDistance;
}

/**
 * 获取相邻节点间的距离数组
 * @param {Array} nodeList - 节点列表，每个节点包含wgs84Lat/wgs84Lng或lat/lng
 * @returns {Array} 距离数组，与nodeList长度相同（最后一个为0）
 */
export function getDistanceArray(nodeList) {
  const distances = [];
  
  if (!nodeList || nodeList.length < 1) {
    return distances;
  }
  
  // 每个节点都有一个距离值（指向下一个节点的距离）
  for (let i = 0; i < nodeList.length - 1; i++) {
    const currentNode = nodeList[i];
    const nextNode = nodeList[i + 1];
    
    // 按优先级使用WGS84坐标，其次使用GCJ02坐标
    let currentLat, currentLng, nextLat, nextLng;
    
    // 当前节点坐标
    if (currentNode.wgs84Lat && currentNode.wgs84Lng) {
      currentLat = currentNode.wgs84Lat;
      currentLng = currentNode.wgs84Lng;
    } else if (currentNode.lat && currentNode.lng) {
      currentLat = currentNode.lat;
      currentLng = currentNode.lng;
    } else {
      distances.push(0);
      continue;
    }
    
    // 下一节点坐标
    if (nextNode.wgs84Lat && nextNode.wgs84Lng) {
      nextLat = nextNode.wgs84Lat;
      nextLng = nextNode.wgs84Lng;
    } else if (nextNode.lat && nextNode.lng) {
      nextLat = nextNode.lat;
      nextLng = nextNode.lng;
    } else {
      distances.push(0);
      continue;
    }
    
    const distance = calculateDistance(currentLat, currentLng, nextLat, nextLng);
    distances.push(distance);
  }
  
  // 最后一个节点没有后续节点，距离为0
  distances.push(0);
  
  return distances;
}
